import { EntityType } from '../../enums/enums';
import {
  ApiResponse,
  AttachmentData,
  BulkUploadResponse,
  UploadFileResponse,
} from '../../types/types';
import { client } from '../client';
import { urls } from '../endpoints';

export const uploadFileRequest = async (
  file: File,
  entityType: EntityType,
  entityId: number,
  description: string,
  isPrimary: boolean,
  displayOrder: number,
  isProfilePicture: boolean,
): Promise<ApiResponse<UploadFileResponse> | null> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType.toString());
    formData.append('entityId', entityId.toString());
    formData.append('description', description);
    formData.append('isPrimary', isPrimary.toString());
    formData.append('displayOrder', displayOrder.toString());
    formData.append('isProfilePicture', isProfilePicture.toString());

    const response = await client.post<ApiResponse<UploadFileResponse>>(
      urls.uploadAttachment,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error uploading file:', error);
    return null;
  }
};

export const bulkUploadFilesRequest = async (
  files: File[],
  entityType: EntityType,
  entityId: number,
  description: string,
  fileOrders: { fileName: string; displayOrder: number; isPrimary: boolean }[],
): Promise<ApiResponse<BulkUploadResponse> | null> => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('EntityType', entityType.toString());
    formData.append('EntityId', entityId.toString());
    formData.append('Description', description);
    fileOrders.forEach((file, index) => {
      formData.append(`FileOrders[${index}].FileName`, file.fileName ?? '');
      formData.append(
        `FileOrders[${index}].DisplayOrder`,
        file?.displayOrder?.toString(),
      );
      formData.append(
        `FileOrders[${index}].IsPrimary`,
        file?.isPrimary?.toString(),
      );
    });
    const response = await client.post<ApiResponse<BulkUploadResponse>>(
      urls.bulkUploadAttachments,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error bulk uploading files:', error);
    return null;
  }
};

export const updateAttachmentRequest = async (
  attachmentId: number,
  data: { description: string; isPrimary: boolean; displayOrder: number },
): Promise<ApiResponse<number> | null> => {
  try {
    const response = await client.put<ApiResponse<number>>(
      urls.updateAttachment(attachmentId),
      data,
    );
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error updating attachment:', error);
    return null;
  }
};

export const deleteAttachmentRequest = async (attachmentId: number) => {
  try {
    const response = await client.delete<ApiResponse<number>>(
      urls.deleteAttachment(attachmentId),
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAttachmentByEntityIdRequest = async (
  entityType: string,
  entityId: number,
) => {
  try {
    const response = await client.get<ApiResponse<AttachmentData>>(
      urls.getAttachmentByEntityId(entityType, entityId),
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePrimaryAttachmentRequest = async (
  facilityId: number,
  attachmentId: number,
): Promise<ApiResponse<number> | null> => {
  try {
    const response = await client.post<ApiResponse<number>>(
      urls.updatePrimaryAttachment(facilityId, attachmentId),
    );
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error updating primary attachment:', error);
    return null;
  }
};
